import React from "react";
import type {
  AvailableFilters,
  AvailableFilter,
  FilterOption,
  SimpleSearchFilters,
  SimpleSearchSortKey,
} from "../lib/types";
import { useLocale } from "../context/LocaleContext";
import { formatMoney } from "../lib/locale";
import { getFilterKeyLabel, isColorFilter, getColorLabel, normalizeHex } from "../lib/filterPresentation";
import { Plus, Minus } from "lucide-react";

type PanelMode = "filter" | "sort";

interface SimpleSearchRefinementsProps {
  mode: PanelMode;
  onModeChange: (mode: PanelMode) => void;
  availableFilters: AvailableFilters;
  activeFilters: SimpleSearchFilters;
  activeSort: SimpleSearchSortKey;
  onToggleFilter: (key: string, value: string) => void;
  onRangeChange: (key: string, min?: number, max?: number) => void;
  onSortChange: (sort: SimpleSearchSortKey) => void;
  loading?: boolean;
  hideToggleButtons?: boolean;
}

const SORT_OPTIONS: Array<{ value: SimpleSearchSortKey; key: string }> = [
  { value: "relevance", key: "simple_search.sort.relevance" },
  { value: "price_asc", key: "simple_search.sort.price_asc" },
  { value: "price_desc", key: "simple_search.sort.price_desc" },
  { value: "recent", key: "simple_search.sort.recent" },
];

export function SimpleSearchRefinements({
  mode,
  onModeChange,
  availableFilters,
  activeFilters,
  activeSort,
  onToggleFilter,
  onRangeChange,
  onSortChange,
  loading = false,
  hideToggleButtons = false,
}: SimpleSearchRefinementsProps) {
  const { t } = useLocale();
  const [openSections, setOpenSections] = React.useState<Set<string>>(new Set());
  const filterToggleLabel = t("simple_search.filter_toggle");
  const sortToggleLabel = t("simple_search.sort_toggle");

  const toggleSection = React.useCallback((key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const filterKeys = React.useMemo(() => Object.keys(availableFilters), [availableFilters]);

  return (
    <div className="ww-simple-search-refinements h-full flex flex-col">
      {!hideToggleButtons && (
        <div className="flex items-center gap-2 pb-3 border-b">
          <button
            type="button"
            className={`ww-simple-search-refinements__toggle ${mode === "filter" ? "is-active" : ""}`}
            onClick={() => onModeChange("filter")}
            aria-pressed={mode === "filter"}
          >
            <span>{filterToggleLabel}</span>
            <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`ww-simple-search-refinements__toggle ${mode === "sort" ? "is-active" : ""}`}
            onClick={() => onModeChange("sort")}
            aria-pressed={mode === "sort"}
          >
            <span>{sortToggleLabel}</span>
            <Plus className="ww-simple-search-refinements__toggle-icon" aria-hidden="true" />
          </button>
        </div>
      )}

      <div className={`${hideToggleButtons ? "" : "pt-3"} flex-1 overflow-y-auto`}>
        {mode === "filter" ? (
          <div className="flex flex-col">
            {filterKeys.map((key) => (
              <FilterAccordionItem
                key={key}
                filterKey={key}
                filter={availableFilters[key]}
                activeValue={activeFilters[key]}
                isOpen={openSections.has(key)}
                onToggleOpen={() => toggleSection(key)}
                onToggleFilter={onToggleFilter}
                onRangeChange={onRangeChange}
                disabled={loading}
              />
            ))}
          </div>
        ) : (
          <div className="pt-1 pl-[10px] flex flex-col gap-3" role="radiogroup" aria-label={t("simple_search.sort")}>
            {SORT_OPTIONS.map((option) => (
              <label key={option.value} className="ww-type-body-sm flex items-center gap-2">
                <input
                  type="radio"
                  name="simple-search-sort"
                  checked={activeSort === option.value}
                  onChange={() => onSortChange(option.value)}
                />
                <span>{t(option.key)}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Accordion Item ---

interface FilterAccordionItemProps {
  filterKey: string;
  filter: AvailableFilter;
  activeValue: SimpleSearchFilters[string] | undefined;
  isOpen: boolean;
  onToggleOpen: () => void;
  onToggleFilter: (key: string, value: string) => void;
  onRangeChange: (key: string, min?: number, max?: number) => void;
  disabled: boolean;
}

function FilterAccordionItem({
  filterKey,
  filter,
  activeValue,
  isOpen,
  onToggleOpen,
  onToggleFilter,
  onRangeChange,
  disabled,
}: FilterAccordionItemProps) {
  const { t } = useLocale();
  const label = getFilterKeyLabel(filterKey, t);

  return (
    <div className="border-t" style={{ borderColor: "var(--color-border, #e5e5e5)" }}>
      <button
        type="button"
        className="w-full flex items-center justify-between py-4 text-left"
        onClick={onToggleOpen}
        aria-expanded={isOpen}
        style={{
          fontFamily: "var(--font-body)",
          background: isOpen ? "var(--color-surface, #f5f5f5)" : "transparent",
          padding: "16px 8px",
          transition: "background-color 180ms ease",
        }}
      >
        <span className="ww-type-body-sm">{label}</span>
        {isOpen
          ? <Minus className="w-[18px] h-[18px] flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
          : <Plus className="w-[18px] h-[18px] flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
        }
      </button>
      {isOpen && (
        <div className="ww-simple-search-refinements__accordion-content px-2 pb-4">
          {filter.type === "range" ? (
            <RangeFilterContent
              filterKey={filterKey}
              min={filter.min ?? 0}
              max={filter.max ?? 100}
              activeValue={activeValue}
              onChange={onRangeChange}
              disabled={disabled}
            />
          ) : (
            <MultiSelectFilterContent
              filterKey={filterKey}
              values={filter.values}
              activeValue={activeValue}
              onToggle={onToggleFilter}
              disabled={disabled}
            />
          )}
        </div>
      )}
    </div>
  );
}

// --- Multi-select Filter ---

interface MultiSelectFilterContentProps {
  filterKey: string;
  values: FilterOption[];
  activeValue: SimpleSearchFilters[string] | undefined;
  onToggle: (key: string, value: string) => void;
  disabled: boolean;
}

function MultiSelectFilterContent({
  filterKey,
  values,
  activeValue,
  onToggle,
  disabled,
}: MultiSelectFilterContentProps) {
  const { t } = useLocale();
  const selected = Array.isArray(activeValue) ? activeValue : [];
  const isColor = isColorFilter(filterKey);

  return (
    <div className="flex flex-col gap-2 pt-2">
      {values.map((option) => {
        const isChecked = selected.includes(option.key);

        if (isColor) {
          return (
            <label
              key={option.key}
              className="ww-type-body-sm flex items-center gap-3 cursor-pointer"
              style={{ opacity: disabled ? 0.5 : 1 }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggle(filterKey, option.key)}
                disabled={disabled}
                className="w-[18px] h-[18px] rounded border-2 accent-black"
                style={{ borderColor: "var(--color-border, #d4d4d4)" }}
              />
              <span
                className="ww-simple-search-color-swatch"
                style={{
                  backgroundColor: normalizeHex(option.key),
                }}
              />
              <span className="ww-type-body-sm">
                {getColorLabel(option.key, t)}
              </span>
            </label>
          );
        }

        return (
          <label
            key={option.key}
            className="ww-type-body-sm flex items-center gap-3 cursor-pointer"
            style={{ opacity: disabled ? 0.5 : 1 }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => onToggle(filterKey, option.key)}
              disabled={disabled}
              className="w-[18px] h-[18px] rounded border-2 accent-black"
              style={{ borderColor: "var(--color-border, #d4d4d4)" }}
            />
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}

// --- Range Filter ---

interface RangeFilterContentProps {
  filterKey: string;
  min: number;
  max: number;
  activeValue: SimpleSearchFilters[string] | undefined;
  onChange: (key: string, min?: number, max?: number) => void;
  disabled: boolean;
}

function RangeFilterContent({
  filterKey,
  min: rangeMin,
  max: rangeMax,
  activeValue,
  onChange,
  disabled,
}: RangeFilterContentProps) {
  const activeRange = !Array.isArray(activeValue) && activeValue ? activeValue : {};
  const currentLow = typeof activeRange.min === "number" ? activeRange.min : rangeMin;
  const currentHigh = typeof activeRange.max === "number" ? activeRange.max : rangeMax;

  const [localLow, setLocalLow] = React.useState(currentLow);
  const [localHigh, setLocalHigh] = React.useState(currentHigh);
  const [isDragging, setIsDragging] = React.useState(false);

  // Sync local state with props when not dragging
  React.useEffect(() => {
    if (!isDragging) {
      setLocalLow(currentLow);
      setLocalHigh(currentHigh);
    }
  }, [currentLow, currentHigh, isDragging]);

  const commitRange = React.useCallback((low: number, high: number) => {
    if (low <= rangeMin && high >= rangeMax) {
      onChange(filterKey, undefined, undefined);
    } else {
      onChange(filterKey, low, high);
    }
  }, [filterKey, onChange, rangeMin, rangeMax]);

  const handleInputBlur = React.useCallback(() => {
    const clampedLow = Math.max(rangeMin, Math.min(localLow, localHigh));
    const clampedHigh = Math.min(rangeMax, Math.max(localHigh, clampedLow));
    setLocalLow(clampedLow);
    setLocalHigh(clampedHigh);
    commitRange(clampedLow, clampedHigh);
  }, [localLow, localHigh, rangeMin, rangeMax, commitRange]);

  const handleInputKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  }, []);

  return (
    <div className="pt-2 flex flex-col gap-3" style={{ opacity: disabled ? 0.5 : 1 }}>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={localLow}
          onChange={(e) => setLocalLow(Number(e.target.value) || rangeMin)}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          min={rangeMin}
          max={rangeMax}
          disabled={disabled}
          className="w-full border rounded px-3 py-2 text-[14px] text-center"
          style={{
            borderColor: "var(--color-border, #d4d4d4)",
            background: "var(--color-surface, #f5f5f5)",
            fontFamily: "var(--font-body)",
          }}
        />
        <span className="text-[14px]" style={{ color: "var(--text-secondary)" }}>-</span>
        <input
          type="number"
          value={localHigh}
          onChange={(e) => setLocalHigh(Number(e.target.value) || rangeMax)}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          min={rangeMin}
          max={rangeMax}
          disabled={disabled}
          className="w-full border rounded px-3 py-2 text-[14px] text-center"
          style={{
            borderColor: "var(--color-border, #d4d4d4)",
            background: "var(--color-surface, #f5f5f5)",
            fontFamily: "var(--font-body)",
          }}
        />
      </div>

      <DualRangeSlider
        min={rangeMin}
        max={rangeMax}
        low={localLow}
        high={localHigh}
        onDrag={(low, high) => {
          setIsDragging(true);
          setLocalLow(low);
          setLocalHigh(high);
        }}
        onCommit={(low, high) => {
          setIsDragging(false);
          commitRange(low, high);
        }}
        disabled={disabled}
      />

      <div className="flex justify-between text-[13px]" style={{ color: "var(--text-secondary)", fontFamily: "var(--font-body)" }}>
        <span>{formatMoney(localLow)}</span>
        <span>{formatMoney(localHigh)}</span>
      </div>
    </div>
  );
}

// --- Dual-thumb Range Slider ---

interface DualRangeSliderProps {
  min: number;
  max: number;
  low: number;
  high: number;
  onDrag: (low: number, high: number) => void;
  onCommit: (low: number, high: number) => void;
  disabled?: boolean;
}

function DualRangeSlider({ min, max, low, high, onDrag, onCommit, disabled }: DualRangeSliderProps) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef<"low" | "high" | null>(null);
  const lowRef = React.useRef(low);
  const highRef = React.useRef(high);

  React.useEffect(() => {
    lowRef.current = low;
    highRef.current = high;
  }, [low, high]);

  const THUMB_R = 10;

  const positionToValue = React.useCallback((clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    const usable = rect.width - THUMB_R * 2;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left - THUMB_R) / usable));
    return Math.round(min + pct * (max - min));
  }, [min, max]);

  const handlePointerDown = React.useCallback((thumb: "low" | "high") => (e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    draggingRef.current = thumb;
  }, [disabled]);

  const handlePointerMove = React.useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const val = positionToValue(e.clientX);
    if (draggingRef.current === "low") {
      const clamped = Math.min(val, highRef.current);
      lowRef.current = clamped;
      onDrag(clamped, highRef.current);
    } else {
      const clamped = Math.max(val, lowRef.current);
      highRef.current = clamped;
      onDrag(lowRef.current, clamped);
    }
  }, [positionToValue, onDrag]);

  const handlePointerUp = React.useCallback(() => {
    if (draggingRef.current) {
      draggingRef.current = null;
      onCommit(lowRef.current, highRef.current);
    }
  }, [onCommit]);

  const range = max - min || 1;
  const lowFrac = (low - min) / range;
  const highFrac = (high - min) / range;
  const thumbPos = (frac: number) => `calc(${THUMB_R}px + ${frac * 100}% - ${frac * THUMB_R * 2}px)`;

  return (
    <div
      ref={trackRef}
      className="relative h-[40px] select-none touch-none"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* Background track */}
      <div
        className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[3px] rounded"
        style={{ background: "var(--color-border, #d4d4d4)" }}
      />
      {/* Active range */}
      <div
        className="absolute top-1/2 -translate-y-1/2 h-[3px] rounded bg-black"
        style={{ left: thumbPos(lowFrac), right: `calc(100% - ${THUMB_R}px - ${highFrac * 100}% + ${highFrac * THUMB_R * 2}px)` }}
      />
      {/* Low thumb */}
      <div
        className="absolute top-1/2 -translate-y-1/2 w-[20px] h-[20px] bg-black rounded-full cursor-pointer"
        style={{ left: thumbPos(lowFrac), marginLeft: -THUMB_R }}
        onPointerDown={handlePointerDown("low")}
      />
      {/* High thumb */}
      <div
        className="absolute top-1/2 -translate-y-1/2 w-[20px] h-[20px] bg-black rounded-full cursor-pointer"
        style={{ left: thumbPos(highFrac), marginLeft: -THUMB_R }}
        onPointerDown={handlePointerDown("high")}
      />
    </div>
  );
}
